// src/components/BillingHistory.jsx
import React, { Component } from "react";
import axios from "axios";
import { toast } from "react-toastify";

class BillingHistory extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      balance: 0,
      subscriptions: [],
      invoices: [],
      creditPurchases: [],
      creditUsage: [],
      error: null,
      portalBusy: false,
      sendReceiptBusyId: null,
    };

    this.API_BASE = process.env.REACT_APP_API_ENDPOINT;
  }

  componentDidMount() {
    this.loadAll();
  }

  /* ==============================
     LOAD ALL BILLING DATA
  ============================== */
  async loadAll() {
    try {
      const [
        balanceRes,
        subsRes,
        creditPayRes,
        invoicesRes,
        usageRes,
      ] = await Promise.allSettled([
        axios.get(`${this.API_BASE}/stripe/credits/balance`, {
          withCredentials: true,
        }),
        axios.get(`${this.API_BASE}/stripe/subscriptions/history`, {
          withCredentials: true,
        }),
        axios.get(`${this.API_BASE}/stripe/payments/credits/history`, {
          withCredentials: true,
        }),
        axios.get(`${this.API_BASE}/stripe/invoices`, {
          withCredentials: true,
        }),
        axios.get(`${this.API_BASE}/stripe/credits/history?limit=50`, {
          withCredentials: true,
        }),
      ]);

      const balance =
        balanceRes.status === "fulfilled"
          ? balanceRes.value?.data?.credits ?? 0
          : 0;

      let subscriptions = [];
      let invoicesFromSubs = [];
      let subscriptionMessage = null;

      if (subsRes.status === "fulfilled") {
        const data = subsRes.value.data || {};
        subscriptions = data.subscriptions || [];
        invoicesFromSubs = data.invoices || [];
        subscriptionMessage = data.message || null;
      }

      let invoices = invoicesFromSubs;
      if (!invoices.length && invoicesRes.status === "fulfilled") {
        invoices = invoicesRes.value.data?.invoices || [];
      }

      const creditPurchases =
        creditPayRes.status === "fulfilled"
          ? creditPayRes.value.data?.creditPurchases || []
          : [];

      const creditUsage =
        usageRes.status === "fulfilled"
          ? usageRes.value.data?.usage || []
          : [];

      this.setState({
        balance,
        subscriptions,
        invoices,
        creditPurchases,
        creditUsage,
        subscriptionMessage,
        loading: false,
        error: null,
      });
    } catch (err) {
      console.error("Error loading billing data:", err);
      this.setState({
        error: "Unable to load billing data",
        loading: false,
      });
    }
  }

  /* ==============================
     OPEN STRIPE BILLING PORTAL
  ============================== */
  handleOpenPortal = async () => {
    try {
      this.setState({ portalBusy: true });

      const res = await axios.post(
        `${this.API_BASE}/stripe/portal/session`,
        {},
        { withCredentials: true }
      );

      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Unable to open billing portal.");
      }
    } catch (err) {
      console.error("Portal error", err);
      toast.error("Unable to open billing portal.");
    } finally {
      this.setState({ portalBusy: false });
    }
  };

  /* ==============================
     DATE FORMATTER
  ============================== */
  formatDate(ts) {
    if (!ts) return "-";
    const d = new Date(ts * 1000 || ts);
    return d.toLocaleString();
  }

  /* ==============================
     SHARED SECTION WRAPPER
  ============================== */
  renderSectionShell(title, subtitle, right, children) {
    return (
      <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lg">
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-sky-900 px-5 py-4 text-white">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h3 className="text-xl font-bold tracking-tight">{title}</h3>
              {subtitle && (
                <p className="mt-1 text-sm text-white/80">{subtitle}</p>
              )}
            </div>
            {right}
          </div>
        </div>

        <div className="p-5">{children}</div>
      </section>
    );
  }

  /* ==============================
     CREDIT BALANCE SECTION
  ============================== */
  renderCreditBalance() {
    return this.renderSectionShell(
      "Credits",
      "Your current AI credit balance and usage context.",
      <div className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide">
        Balance
      </div>,
      <div className="rounded-2xl border border-sky-100 bg-sky-50/70 p-5">
        <div className="flex items-end gap-3">
          <span className="text-sm font-medium text-slate-500">
            Current balance
          </span>
          <span className="text-4xl font-bold text-slate-900">
            {this.state.balance}
          </span>
        </div>

        <p className="mt-3 text-sm text-slate-600">
          Credits are consumed when you use AI, voice, and automated processing
          features in Caredevo.
        </p>
      </div>
    );
  }

  /* ==============================
     SUBSCRIPTIONS SECTION
  ============================== */
  renderSubscriptions() {
    const { subscriptions, invoices, portalBusy } = this.state;

    return this.renderSectionShell(
      "Subscriptions",
      "Your current or historical subscription records.",
      <button
        className="rounded-2xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm ring-1 ring-white/20 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
        disabled={portalBusy}
        onClick={this.handleOpenPortal}
      >
        {portalBusy ? "Opening portal…" : "Manage in Stripe portal"}
      </button>,
      <>
        {subscriptions.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-500">
            No subscription records found.
          </div>
        )}

        <div className="space-y-4">
          {subscriptions.map((s) => {
            const matchedInvoice = invoices.find(
              (inv) => inv.subscription === s.id
            );

            const invoiceNumber = matchedInvoice?.number || "—";
            const item = s.items?.data?.[0];
            const price = item?.price;

            const amount = price
              ? `${(price.unit_amount / 100).toFixed(2)} ${price.currency.toUpperCase()}`
              : "-";

            return (
              <div
                key={s.id}
                className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4 shadow-sm"
              >
                <div className="mb-3 text-base font-semibold text-slate-900">
                  Invoice: {invoiceNumber}
                </div>

                <div className="grid grid-cols-1 gap-y-2 text-sm text-slate-700 md:grid-cols-2">
                  <div>
                    <span className="font-semibold">Start date: </span>
                    {this.formatDate(s.billing_cycle_anchor)}
                  </div>

                  <div>
                    <span className="font-semibold">Amount: </span>
                    {amount} / {price?.recurring?.interval || "period"}
                  </div>

                  <div>
                    <span className="font-semibold">Next billing: </span>
                    {this.formatDate(s.current_period_end)}
                  </div>

                  <div>
                    <span className="font-semibold">End date: </span>
                    {s.cancel_at
                      ? this.formatDate(s.cancel_at)
                      : "Active (no end date)"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  }

  /* ==============================
     CREDIT PURCHASES
  ============================== */
  renderCreditPurchases() {
    const { creditPurchases, invoices } = this.state;

    const invoiceById = {};
    const invoiceByPaymentIntent = {};

    invoices.forEach((inv) => {
      if (!inv) return;
      invoiceById[inv.id] = inv;
      if (inv.payment_intent) {
        invoiceByPaymentIntent[inv.payment_intent] = inv;
      }
    });

    return this.renderSectionShell(
      "Credit Purchases",
      "Your one-off credit top-ups and related invoices.",
      <div className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide">
        Purchases
      </div>,
      <div className="overflow-x-auto rounded-2xl border border-slate-200">
        {creditPurchases.length === 0 ? (
          <div className="bg-slate-50 p-6 text-sm text-slate-500">
            No credit top-ups yet.
          </div>
        ) : (
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left">
                <th className="px-3 py-3 font-semibold text-slate-700">Invoice</th>
                <th className="px-3 py-3 font-semibold text-slate-700">Date</th>
                <th className="px-3 py-3 font-semibold text-slate-700">Amount</th>
                <th className="px-3 py-3 font-semibold text-slate-700">Status</th>
                <th className="px-3 py-3 font-semibold text-slate-700">Credits</th>
                <th className="px-3 py-3 font-semibold text-slate-700">Invoice/Receipt</th>
              </tr>
            </thead>
            <tbody>
              {creditPurchases.map((s) => {
                const matchedInvoice =
                  (s.invoice && invoiceById[s.invoice]) ||
                  (s.payment_intent && invoiceByPaymentIntent[s.payment_intent]);

                return (
                  <tr key={s.id} className="border-b border-slate-100 bg-white">
                    <td className="break-all px-3 py-3 font-medium text-slate-900">
                      {matchedInvoice
                        ? matchedInvoice.number || matchedInvoice.id
                        : "-"}
                    </td>
                    <td className="px-3 py-3">{this.formatDate(s.created)}</td>
                    <td className="px-3 py-3">
                      {s.amount_total
                        ? `${(s.amount_total / 100).toFixed(2)} ${s.currency?.toUpperCase()}`
                        : "-"}
                    </td>
                    <td className="px-3 py-3 capitalize">{s.payment_status}</td>
                    <td className="px-3 py-3">
                      {s.metadata?.creditsAmount || "-"}
                    </td>
                    <td className="px-3 py-3">
                      {matchedInvoice?.hosted_invoice_url ? (
                        <a
                          href={matchedInvoice.hosted_invoice_url}
                          target="_blank"
                          rel="noreferrer"
                          className="font-medium text-sky-700 hover:text-sky-800"
                        >
                          View
                        </a>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  /* ==============================
     SUBSCRIPTION HISTORY
  ============================== */
  renderSubscriptionHistory() {
    const { invoices } = this.state;

    const subscriptionInvoices = (invoices || []).filter(
      (inv) => !!inv.subscription
    );

    return this.renderSectionShell(
      "Subscription History",
      "Past subscription invoices and receipts.",
      <div className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide">
        Invoices
      </div>,
      <div className="overflow-x-auto rounded-2xl border border-slate-200">
        {subscriptionInvoices.length === 0 ? (
          <div className="bg-slate-50 p-6 text-sm text-slate-500">
            No subscription invoices yet.
          </div>
        ) : (
          <table className="min-w-full border-collapse text-sm text-left">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="px-3 py-3 font-semibold text-slate-700">Invoice</th>
                <th className="px-3 py-3 font-semibold text-slate-700">Date</th>
                <th className="px-3 py-3 font-semibold text-slate-700">Amount</th>
                <th className="px-3 py-3 font-semibold text-slate-700">Status</th>
                <th className="px-3 py-3 font-semibold text-slate-700">Invoice/Receipt</th>
              </tr>
            </thead>
            <tbody>
              {subscriptionInvoices.map((inv) => (
                <tr key={inv.id} className="border-b border-slate-100 bg-white">
                  <td className="break-all px-3 py-3">
                    {inv.number || inv.id}
                  </td>
                  <td className="px-3 py-3">{this.formatDate(inv.created)}</td>
                  <td className="px-3 py-3">
                    {(inv.total / 100).toFixed(2)} {inv.currency?.toUpperCase()}
                  </td>
                  <td className="px-3 py-3 capitalize">{inv.status}</td>
                  <td className="px-3 py-3">
                    {inv.hosted_invoice_url ? (
                      <a
                        href={inv.hosted_invoice_url}
                        target="_blank"
                        rel="noreferrer"
                        className="font-medium text-sky-700 hover:text-sky-800"
                      >
                        View
                      </a>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  /* ==============================
     CREDIT USAGE
  ============================== */
  renderCreditUsage() {
    const { creditUsage } = this.state;

    return this.renderSectionShell(
      "Credit Usage",
      "Recent AI and processing credit consumption.",
      <div className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide">
        Usage
      </div>,
      <div className="overflow-x-auto rounded-2xl border border-slate-200">
        {creditUsage.length === 0 ? (
          <div className="bg-slate-50 p-6 text-sm text-slate-500">
            No usage logged yet.
          </div>
        ) : (
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left">
                <th className="px-3 py-3 font-semibold text-slate-700">Date</th>
                <th className="px-3 py-3 font-semibold text-slate-700">Amount</th>
                <th className="px-3 py-3 font-semibold text-slate-700">Type</th>
                <th className="px-3 py-3 font-semibold text-slate-700">Reason</th>
              </tr>
            </thead>
            <tbody>
              {creditUsage.map((u) => (
                <tr key={u._id} className="border-b border-slate-100 bg-white">
                  <td className="px-3 py-3">
                    {new Date(u.createdAt).toLocaleString()}
                  </td>
                  <td className="px-3 py-3">{u.amount}</td>
                  <td className="px-3 py-3">{u.type}</td>
                  <td className="px-3 py-3">
                    {u.reason || <span className="text-slate-400">-</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  /* ==============================
     SEND RECEIPT VIA EMAIL
  ============================== */
  handleSendReceipt = async (chargeId) => {
    if (!chargeId) {
      toast.error("No charge id found for this payment.");
      return;
    }

    try {
      this.setState({ sendReceiptBusyId: chargeId });

      await axios.post(
        `${this.API_BASE}/stripe/receipts/${chargeId}/send`,
        {},
        { withCredentials: true }
      );

      toast.success("Receipt emailed to customer.");
    } catch (err) {
      console.error("Send receipt error", err);
      const msg =
        err.response?.data?.error ||
        err.message ||
        "Unable to send receipt.";
      toast.error(msg);
    } finally {
      this.setState({ sendReceiptBusyId: null });
    }
  };

  /* ==============================
     MAIN RENDER
  ============================== */
  render() {
    const { loading, error } = this.state;

    if (loading) {
      return (
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-6 text-center text-slate-600 shadow-sm">
          Loading billing data…
        </div>
      );
    }

    if (error) {
      return (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm">
          {error}
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
          <div className="bg-gradient-to-r from-slate-900 via-sky-900 to-cyan-800 px-6 py-8 text-white">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-3xl font-bold tracking-tight">
                Billing & Member History
              </h2>
              <p className="mt-2 text-sm text-white/80">
                Review credits, subscriptions, invoices, and recent usage in one place.
              </p>
            </div>
          </div>
        </section>
        <div className='grid grid-cols-2 gap-4 xs:grid-cols-1 sm:grid-cols-1 '>
        {this.renderCreditBalance()}
        {this.renderSubscriptions()}
        {this.renderSubscriptionHistory()}
        {this.renderCreditPurchases()}
   
        {this.renderCreditUsage()}
   
        </div>
      </div>
    );
  }
}

export default BillingHistory;